import React, { useEffect, useState } from 'react';
import API from 'shared/api';
import { useAuth } from 'shared/context/AuthContext';
import { FiUserCheck, FiClock, FiDollarSign, FiAlertTriangle } from 'react-icons/fi';
import { COL } from 'shared/darkColors';
import {
  S, money, intFmt, Kpi, SectionCard, Cargando, ErrorMsg, buildQuery, descargarCSV,
} from './reportesCommon';

export default function ReporteUsuarios({ filtros }) {
  const { isTipoEmpresa, tienePermiso } = useAuth();
  const mostrarPropinas = isTipoEmpresa('restaurante') && tienePermiso('propinas.ver');
  const [tab, setTab] = useState('rendimiento');
  const [rendimiento, setRendimiento] = useState([]);
  const [asistencia, setAsistencia] = useState([]);
  const [propinas, setPropinas] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(false);
    if (tab === 'rendimiento') {
      API.get(`/reportes/usuarios/rendimiento?${buildQuery(filtros).toString()}`)
        .then((r) => alive && r.data.status === 'success' && setRendimiento(r.data.data.usuarios || []))
        .catch(() => alive && setError(true))
        .finally(() => alive && setLoading(false));
    } else if (tab === 'propinas') {
      API.get(`/propinas/reporte?${buildQuery(filtros).toString()}`)
        .then((r) => alive && r.data.status === 'success' && setPropinas(r.data.data || null))
        .catch(() => alive && setError(true))
        .finally(() => alive && setLoading(false));
    } else {
      API.get('/asistencia/usuarios-estado')
        .then((r) => alive && r.data.status === 'success' && setAsistencia(r.data.data || []))
        .catch(() => alive && setError(true))
        .finally(() => alive && setLoading(false));
    }
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, filtros.sede, filtros.fechaDesde, filtros.fechaHasta]);

  if (loading) return <Cargando />;
  if (error) return <ErrorMsg />;

  const exportar = () => {
    if (tab === 'rendimiento') {
      descargarCSV(
        'reporte_rendimiento_usuarios.csv',
        ['Usuario', 'Monto', 'Transacciones', 'Unidades', 'Ticket promedio'],
        rendimiento.map((u) => [u.nombre, u.monto, u.transacciones, u.unidades, u.ticket_promedio])
      );
    } else if (tab === 'propinas') {
      descargarCSV(
        'reporte_propinas.csv',
        ['Usuario', 'Propinas', 'Total (equiv USD)'],
        (propinas?.por_usuario || []).map((u) => [u.nombre, u.n_propinas, u.total_equiv])
      );
    } else {
      descargarCSV(
        'reporte_asistencia.csv',
        ['Usuario', 'Rol', 'Sede', 'Estado', 'Última marca'],
        asistencia.map((a) => [a.nombre, a.rol, a.sede_asignada, a.activo ? 'Activo' : 'Inactivo', a.ultima_hora])
      );
    }
  };

  const activos = asistencia.filter((a) => a.activo).length;
  const tarde = asistencia.filter((a) => a.tarde).length;

  return (
    <div>
      {tab === 'asistencia' && (
        <div style={S.kpiGrid}>
          <Kpi label="Usuarios" value={intFmt(asistencia.length)} color={COL.pri} />
          <Kpi label="Activos" value={intFmt(activos)} color={COL.green} />
          <Kpi label="Con retraso" value={intFmt(tarde)} color={COL.amber} />
        </div>
      )}

      {tab === 'propinas' && propinas && (
        <div style={S.kpiGrid}>
          <Kpi label="Total propinas" value={money(propinas.total_equiv)} color={COL.green} />
          <Kpi label="Propinas" value={intFmt(propinas.n_propinas)} color={COL.pri} />
          <Kpi label="Usuarios" value={intFmt(propinas.n_usuarios)} color={COL.pri} />
          <Kpi label="Promedio" value={money(propinas.promedio)} color={COL.amber} />
        </div>
      )}

      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '14px' }}>
        <button onClick={() => setTab('rendimiento')} style={{ ...S.btn, ...(tab === 'rendimiento' ? S.btnActive : {}) }}>
          <FiUserCheck size={12} style={{ verticalAlign: '-2px' }} /> Rendimiento
        </button>
        <button onClick={() => setTab('asistencia')} style={{ ...S.btn, ...(tab === 'asistencia' ? S.btnActive : {}) }}>
          <FiClock size={12} style={{ verticalAlign: '-2px' }} /> Asistencia
        </button>
        {mostrarPropinas && (
          <button onClick={() => setTab('propinas')} style={{ ...S.btn, ...(tab === 'propinas' ? S.btnActive : {}) }}>
            <FiDollarSign size={12} style={{ verticalAlign: '-2px' }} /> Propinas
          </button>
        )}
        <div style={{ flex: 1 }} />
        <button onClick={exportar} style={S.btn}>Exportar CSV</button>
      </div>

      {tab === 'rendimiento' && (
        <SectionCard title="Rendimiento por usuario" icon={FiUserCheck} color={COL.pri} hint="Ventas gestionadas por cajero/asesor">
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>{['Usuario', 'Monto', 'Transacciones', 'Unidades', 'Ticket promedio'].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {rendimiento.map((u) => (
                  <tr key={u.cedula}>
                    <td style={{ ...S.td, fontWeight: 600 }}>{u.nombre}</td>
                    <td style={{ ...S.td, fontWeight: 700, color: COL.green }}>{money(u.monto)}</td>
                    <td style={S.td}>{intFmt(u.transacciones)}</td>
                    <td style={S.td}>{intFmt(u.unidades)}</td>
                    <td style={S.td}>{money(u.ticket_promedio)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rendimiento.length === 0 && <div style={{ textAlign: 'center', padding: '20px', color: COL.muted }}>Sin actividad en el período</div>}
        </SectionCard>
      )}

      {tab === 'propinas' && (
        <>
          <SectionCard title="Propinas por usuario" icon={FiDollarSign} color={COL.green} hint="Solo restaurantes">
            <div style={S.tableWrap}>
              <table style={S.table}>
                <thead>
                  <tr>{['Usuario', 'Propinas', 'Total (equiv USD)'].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {(propinas?.por_usuario || []).map((u) => (
                    <tr key={u.cedula}>
                      <td style={{ ...S.td, fontWeight: 600 }}>{u.nombre}</td>
                      <td style={S.td}>{intFmt(u.n_propinas)}</td>
                      <td style={{ ...S.td, fontWeight: 700, color: COL.green }}>{money(u.total_equiv)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {(propinas?.por_usuario || []).length === 0 && (
              <div style={{ textAlign: 'center', padding: '20px', color: COL.muted }}>Sin propinas en el período</div>
            )}
          </SectionCard>

          {(propinas?.detalle || []).length > 0 && (
            <SectionCard title="Detalle de propinas" icon={FiDollarSign} color={COL.pri2} hint="Quién recibió cada propina">
              <div style={S.tableWrap}>
                <table style={S.table}>
                  <thead>
                    <tr>{['Fecha', 'Usuario', 'Origen', 'Monto', 'Pedido'].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {(propinas?.detalle || []).map((p) => (
                      <tr key={p.id}>
                        <td style={{ ...S.td, color: COL.muted, fontSize: '11px' }}>{p.fecha || '—'}</td>
                        <td style={{ ...S.td, fontWeight: 600 }}>{p.nombre}</td>
                        <td style={S.td}>
                          {p.origen_tipo === 'porcentaje' ? '%' : p.origen_tipo === 'cambio' ? 'Cambio' : 'Monto'}
                        </td>
                        <td style={{ ...S.td, fontWeight: 700, color: COL.green }}>{money(p.monto)}</td>
                        <td style={S.td}>{p.pedido_id ? `#${p.pedido_id}` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>
          )}

          {(propinas?.sin_propina_cobrado_de_mas || []).length > 0 && (
            <SectionCard
              title="Cobros de más sin registrar propina"
              icon={FiAlertTriangle}
              color={COL.amber}
              hint={`${(propinas?.sin_propina_cobrado_de_mas || []).length} pedido(s) con exceso no marcado`}
            >
              <div style={S.tableWrap}>
                <table style={S.table}>
                  <thead>
                    <tr>{['Pedido', 'Cajero', 'Total', 'Recibido', 'Exceso'].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {(propinas?.sin_propina_cobrado_de_mas || []).map((p) => (
                      <tr key={p.pedido_id}>
                        <td style={S.td}>#{p.ticket_numero || p.pedido_id}</td>
                        <td style={S.td}>{p.cajero_nombre || '—'}</td>
                        <td style={S.td}>{money(p.total)}</td>
                        <td style={S.td}>{money(p.monto_recibido)}</td>
                        <td style={{ ...S.td, fontWeight: 700, color: COL.amber }}>{money(p.exceso)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>
          )}
        </>
      )}

      {tab === 'asistencia' && (
        <SectionCard title="Estado de asistencia" icon={FiClock} color={COL.pri}>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>{['Usuario', 'Rol', 'Sede', 'Estado', 'Última marca'].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {asistencia.map((a) => (
                  <tr key={a.cedula}>
                    <td style={{ ...S.td, fontWeight: 600 }}>{a.nombre}</td>
                    <td style={{ ...S.td, color: COL.muted }}>{a.rol || '—'}</td>
                    <td style={{ ...S.td, color: COL.muted }}>{a.sede_asignada || '—'}</td>
                    <td style={S.td}>
                      {a.tarde ? (
                        <span style={{ ...S.chip, color: COL.amber, background: COL.bg }}>Con retraso</span>
                      ) : a.activo ? (
                        <span style={{ ...S.chip, color: COL.green, background: COL.bg }}>Activo</span>
                      ) : (
                        <span style={{ ...S.chip, color: COL.muted, background: COL.bg }}>Inactivo</span>
                      )}
                    </td>
                    <td style={{ ...S.td, color: COL.muted, fontSize: '11px' }}>{a.ultima_hora || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {asistencia.length === 0 && <div style={{ textAlign: 'center', padding: '20px', color: COL.muted }}>Sin usuarios</div>}
        </SectionCard>
      )}
    </div>
  );
}

